/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import { createPersistentItem, logDebug, logWarn, type Maybe } from '@amazon/vinyl-util'
import { createLocalOverlay } from '@/util/localOverlay'
import { gql, gqlFetch } from '@/service/gql'
import { getAuth } from '@/auth/authRef'
import type { AgentCard } from '@/blackbolt/cardTypes'
import { isFeedStubEnabled } from '@/blackbolt/feedStubFlag'
import { isAlternateFeedSourceSelected } from '@/blackbolt/feedSourceFlag'
import { publishFeedFeedback } from '@/blackbolt/feedFeedbackChannel'
import {
    isKnownStubRejection as isKnownStubRejectionWith,
    type StubRejectionRules,
} from '@/blackbolt/stubRejection'
import { toast } from '@/components/toast'
import { getStringAsync } from '@/blackbolt/i18n/bundle.generated'
import { toastRelationshipFailure } from '@/blackbolt/actionToasts'
import { ctx } from '@/util/context'

const { target } = ctx('feedback')

/**
 * Firefly-side error codes / message fragments that indicate "the id
 * you sent isn't a real recommendation" — expected outcomes for
 * dev-stub fixtures and some wire heartbeat cards whose `id` isn't a
 * Firefly `Recommendation.id`. For these we keep the user's local
 * decision (feedback overlay + pressed button) but stay quiet — the
 * "request failed, will not retry" toast is not user-actionable here.
 */
const STUB_REJECTION_RULES: StubRejectionRules = {
    codes: new Set(['NOT_FOUND', 'INVALID_INPUT', 'BAD_REQUEST', 'RECOMMENDATION_NOT_FOUND']),
    messageFragments: [
        'not found',
        'unknown recommendation',
        'invalid recommendation',
        'no data available',
    ],
}

/** Classify a recommendation-mutation rejection. See {@link isKnownStubRejectionWith}. */
function isKnownStubRejection(err: unknown): boolean {
    return isKnownStubRejectionWith(err, STUB_REJECTION_RULES)
}

type FeedbackKind = 'up' | 'down'

// Per-card local record of the user's most recent thumb decision, keyed
// by card id. Between a click and the next feed fetch this overlay is
// the only signal a remount has for rendering the button pre-pressed, so
// the TTL is long — this is durable "what the user did on this device"
// state, not a short server-catch-up window like the track-like overlay.
// A validator drops legacy / corrupt entries on hydration; the whole
// keyspace is bulk-cleared on sign-out (see `clearFeedbackOverlay`).
const FEEDBACK_OVERLAY_TTL_MS = 30 * 24 * 60 * 60 * 1000
const feedbackOverlay = createLocalOverlay<string, FeedbackKind>({
    persistence: createPersistentItem({
        key: 'overlay:feedback',
        defaultValue: {},
    }),
    validateValue: (v): v is FeedbackKind => v === 'up' || v === 'down',
})

/**
 * Drop every remembered feedback decision. Called from sign-out teardown
 * so one account's thumb state never bleeds into the next on a shared
 * machine — the in-memory overlay survives the sign-out `localStorage`
 * wipe, so an explicit `clearAll` is required.
 */
export function clearFeedbackOverlay(): void {
    feedbackOverlay.clearAll()
}

const setRecommendationLikeStateMutation = gql(`
    mutation setRecommendationLikeState(
        $likeState: LikeStateType!
        $recommendationId: String!
    ) {
        setRecommendationLikeState(
            likeState: $likeState
            recommendationId: $recommendationId
        ) {
            likeState
        }
    }
`)

/**
 * Options passed by the card layer when a thumb-up/down click fires.
 * `element` is the clicked button — when present we manage its
 * optimistic-state attributes here so every card gets the same
 * treatment without repeating the pattern in each `cards/*.tsx` file.
 */
export interface FeedbackOptions {
    /** The button element that was clicked (for optimistic UI). */
    readonly element?: HTMLElement | null
}

/**
 * Handle a card "thumb up" tap. Records the decision locally, fires
 * the Firefly `setRecommendationLikeState` mutation (unless the app is
 * running against the dev-stub feed), and toasts the acknowledgement.
 * Optimistic state on the button is flipped before the mutation
 * resolves and reverted on failure.
 */
export function thumbUp(card: AgentCard, options: FeedbackOptions = {}): void {
    submitFeedback(card, 'up', options)
}

/** Handle a card "thumb down" tap. Symmetric to `thumbUp`. */
export function thumbDown(card: AgentCard, options: FeedbackOptions = {}): void {
    submitFeedback(card, 'down', options)
}

/**
 * Fire `setRecommendationLikeState` for a bare recommendation id — used
 * by the browse tile overflow menu ("Show more like this" / "Not
 * interested") where there's no `AgentCard` wrapper, no button element
 * to optimistically flip, and no localStorage overlay. Returns the
 * mutation promise so the caller can toast / revert on failure. Silently
 * skipped when the app is running the dev-stub feed or when the user is
 * signed out (parity with `submitFeedback`'s bail-out paths).
 */
export function setRecommendationLike(
    recommendationId: string,
    likeState: 'LIKE' | 'DISLIKE'
): Promise<void> {
    // Dev-fixture ids don't correspond to real Firefly recommendations
    // — firing the mutation would only log a `not found` error.
    if (isFeedStubEnabled()) return Promise.resolve()
    if (!getAuth().isSignedIn()) {
        logWarn(target, 'skipping setRecommendationLikeState — not signed in', { recommendationId })
        return Promise.resolve()
    }
    return gqlFetch(setRecommendationLikeStateMutation, {
        likeState,
        recommendationId,
    }).then(() => undefined)
}

/** Exposed for callers that want to classify errors the same way. */
export { isKnownStubRejection }

function submitFeedback(card: AgentCard, kind: FeedbackKind, options: FeedbackOptions): void {
    logDebug(target, 'feedback', { kind, cardId: card.id })

    // 1. Persist locally first — the write is synchronous, so if the
    //    mutation call later throws we still know what the user
    //    intended when we come back to revert.
    writeLocalFeedback(card.id, kind)

    // 2. Optimistic UI: mark the button as pressed / disabled so the
    //    same click can't fire twice while the mutation is in flight.
    applyOptimisticState(options.element, kind)

    // 3. Feed the decision to the live-feed re-shape channel so a source that
    //    consumes it adjusts what streams in next. Firefly ignores the
    //    channel — it re-shapes server-side off the mutation below.
    publishFeedFeedback({ cardId: card.id, kind, ...(card.title ? { title: card.title } : null) })

    toast(getStringAsync('dmusic_feed_feedback_noted'))

    if (isAlternateFeedSourceSelected()) {
        // A non-Firefly source's card ids aren't Firefly recommendations, so the
        // mutation would only log a `not found`. The local write + optimistic
        // flip + the re-shape signal above are the whole contract in this mode.
        return
    }

    if (isFeedStubEnabled()) {
        // Dev-fixture cards have no matching Firefly recommendation —
        // the localStorage write + toast is the whole contract in
        // this mode. Bail before we build the gql request.
        return
    }

    if (!getAuth().isSignedIn()) {
        // Anonymous users can't own feedback state server-side. Keep
        // the local write so the UI stays consistent on this device;
        // the network hop would only produce a `Not authenticated`
        // error.
        logWarn(target, 'skipping setRecommendationLikeState — not signed in', {
            cardId: card.id,
        })
        return
    }

    const likeState = kind === 'up' ? 'LIKE' : 'DISLIKE'

    gqlFetch(setRecommendationLikeStateMutation, {
        likeState,
        recommendationId: card.id,
    }).catch((err: unknown) => {
        if (isKnownStubRejection(err)) {
            // Dev-stub / heartbeat cards whose `id` isn't a real
            // Firefly recommendation legitimately fail server-side.
            // Keep the local write + pressed state (the user's
            // intent is recorded on-device) and stay quiet — no
            // "request failed, will not retry" toast, since this
            // failure is not user-actionable. Log at warn so the
            // occurrence still lands in telemetry.
            logWarn(target, 'setRecommendationLikeState rejected id (stub / unknown), swallowing', {
                cardId: card.id,
                error: (err as Error).message,
            })
            return
        }
        // Genuine server-side / network failure — revert both the
        // local write and the button state so the UI doesn't lie
        // about a decision the server didn't accept. Surface the
        // branded "Couldn't save. Try again." toast instead of the
        // raw errorHandler message so the failure feedback matches
        // the iOS copy.
        logWarn(target, 'setRecommendationLikeState failed, reverting local feedback', {
            cardId: card.id,
            error: (err as Error).message,
        })
        clearLocalFeedback(card.id)
        revertOptimisticState(options.element)
        toastRelationshipFailure()
    })
}

/**
 * Read the persisted feedback decision for a card, or `undefined` if
 * the user hasn't voted. Exposed for card layouts that want to render
 * the AgentQuery button already in its "clicked" state on remount
 * (e.g. after a page refresh) — the wire mutation is authoritative
 * once we have a signed-in server round-trip, but between clicks and
 * the next feed fetch the local overlay is our only signal.
 */
export function readLocalFeedback(cardId: string): FeedbackKind | undefined {
    return feedbackOverlay.read(cardId)
}

function writeLocalFeedback(cardId: string, kind: FeedbackKind): void {
    feedbackOverlay.write(cardId, kind, { ttlMs: FEEDBACK_OVERLAY_TTL_MS })
}

function clearLocalFeedback(cardId: string): void {
    feedbackOverlay.clear(cardId)
}

/**
 * Flip the button into its "clicked" affordance state. Uses attribute
 * / inline-style updates rather than a new CSS class so every consumer
 * of the card-scoped `.queryAction` inherits this state without a stylesheet edit.
 *
 * `aria-pressed="true"` carries the semantic state for a11y,
 * `disabled` prevents double-fire while the mutation is in flight, and
 * a tiny opacity dip gives a visual cue.
 */
function applyOptimisticState(element: Maybe<HTMLElement>, kind: FeedbackKind): void {
    if (!element) return
    element.setAttribute('aria-pressed', 'true')
    element.setAttribute('data-feedback', kind)
    if (element instanceof HTMLButtonElement) {
        element.disabled = true
    }
    element.style.opacity = '0.55'
}

function revertOptimisticState(element: Maybe<HTMLElement>): void {
    if (!element) return
    element.removeAttribute('aria-pressed')
    element.removeAttribute('data-feedback')
    if (element instanceof HTMLButtonElement) {
        element.disabled = false
    }
    element.style.removeProperty('opacity')
}
